import React from "react";
import * as c from "./blogHomepage.module.scss";
import SEO from "@components/seo";
import { graphql } from "gatsby";
import { BlogLayout, BlogPostCard, BlogPostGrid, Category } from "@components/blog/";
import { EmailCaptureForm } from "@components/shared";
import { getBlogSchema } from "@components/blog/blogSchema";

export default function Blog({ data }) {
  const { contentfulBlogHomepage, allContentfulBlogPost } = data;
  const { featuredArticle, popularArticles, browseArticlesAds } = contentfulBlogHomepage;

  const { Section, Header } = BlogLayout;
  return (
    <BlogLayout className={c.container}>
      <SEO
        title="Blog"
        description="The Mangomint Blog is your home for industry knowledge and community insight on how to run a successful salon or spa business."
      />

      <Header className={c.header}>
        <Header.Headline label="Mangomint Blog" />
        <EmailCaptureForm formSource="blog_subscribe_form" variant="inline" />
      </Header>

      <Section>
        <BlogPostCard post={featuredArticle} key={`featured-${featuredArticle.id}`} size="lg" />
      </Section>

      <Section className={c.section}>
        <Section.Headline label="Popular articles" />
        <BlogPostGrid items={popularArticles} size="md" />
      </Section>

      <Section className={c.section}>
        <Section.Headline label="Browse articles" />
        {/* TODO: Get rid of Category... this just ended up being convoluted */}
        <Category>
          <Category.FilterList />
          <Category.BlogPostGrid posts={allContentfulBlogPost.posts} ads={browseArticlesAds} />
        </Category>
      </Section>
    </BlogLayout>
  );
}

export const blogPostCardFields = graphql`
  fragment blogPostCardFields on ContentfulBlogPost {
    __typename
    id
    slug
    path: gatsbyPath(filePath: "/blog/{contentfulBlogPost.slug}")
    title
    subhead {
      subhead
    }
    categories {
      id
      name
      slug
      path: gatsbyPath(filePath: "/blog/c/{mangomintBlogPostCategory.name}")
    }
    featureImage {
      full: gatsbyImageData(
        quality: 100
        layout: FULL_WIDTH
        # TODO: This is not ideal for performance, but otherwise gatsby grabs the wrong resolution
        # on smaller sizes even though it should be grabbing 2x. Need to revisit and manually tweak
        # all the sizes for each viewport.
        sizes: "1520px"
        # breakpoints: [330, 360, 560, 760]
        # outputPixelDensities: [2]
      )
    }
    author {
      name
      title
      activeProfile
      biography {
        raw
      }
      photo {
        gatsbyImageData(width: 50, quality: 90, layout: CONSTRAINED)
      }
    }
    publishedAt(formatString: "MMMM D, YYYY")
  }
`;

export const query = graphql`
  query {
    contentfulBlogHomepage(version: { eq: "2024" }) {
      featuredArticle {
        ...blogPostCardFields
      }
      popularArticles {
        ...blogPostCardFields
      }
      browseArticlesAds {
        ... on ContentfulLandingPageSection2Column1Image {
          __typename
          id
          label
          heading
          shouldShowImageColumnFirst
          subheading {
            subheading
          }
          wistiaVideoId
          desktopImage {
            # gatsbyImageData(width: 480, quality: 90, layout: CONSTRAINED)
            gatsbyImageData(width: 760, quality: 90, layout: CONSTRAINED)
          }
          mobileImage {
            gatsbyImageData(width: 320, quality: 90, layout: CONSTRAINED)
          }
          ctas {
            __typename
            ... on ContentfulCtaVideoModal {
              id
              label
              wistiaVideoId
              trackedEventName
              style
              showIcon
            }
            ... on ContentfulCtaButton {
              id
              label
              url
              isExternal
              trackedEventName
              style
              showIcon
              openChat
            }
          }
        }
      }
    }
    allContentfulBlogPost(
      sort: { publishedAt: DESC }
      filter: {
        title: { ne: null }
        slug: { ne: null }
        publishedAt: { ne: null }
        author: { name: { ne: null } }
        subhead: { subhead: { ne: null } }
        listOnOverviewPage: { ne: false }
        # categories: { ne: null } # new
        categories: { elemMatch: { id: { ne: null } } }
        blog_homepage: { elemMatch: { version: { ne: "2024" } } }
      }
    ) {
      posts: nodes {
        ...blogPostCardFields
      }
      # categories: distinct(field: { category: SELECT })
      # categories: distinct(field: { categories: { name: SELECT } })
    }
  }
`;

export function Head({ data }) {
  const posts = data?.allContentfulBlogPost?.posts || [];
  const blogSchema = getBlogSchema({
    posts: posts.slice(0, 10),
    blogUrl: "/blog/",
    blogName: "Mangomint Blog",
    blogDescription:
      "The Mangomint Blog is your home for industry knowledge and community insight on how to run a successful salon or spa business.",
    logoUrl: "/images/logo.png",
    publisherUrl: "https://www.mangomint.com/",
  });
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(blogSchema),
        }}
      />
    </>
  );
}
